import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Save, ArrowLeft, Send } from 'lucide-react'
import { WorkType, WorkStatus } from '@/types'

const workSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  type: z.nativeEnum(WorkType),
  content: z.string().min(1, 'Content is required'),
  status: z.nativeEnum(WorkStatus)
})

type WorkForm = z.infer<typeof workSchema>

const workTypeOptions = [
  { value: WorkType.POEM, label: 'Poetry' },
  { value: WorkType.SHORT_STORY, label: 'Short Story' },
  { value: WorkType.ESSAY, label: 'Essay' },
  { value: WorkType.NOVEL, label: 'Novel' },
  { value: WorkType.FLASH_FICTION, label: 'Flash Fiction' }
]

const statusOptions = [
  { value: WorkStatus.DRAFT, label: 'Draft' },
  { value: WorkStatus.READY, label: 'Ready for Submission' },
  { value: WorkStatus.SUBMITTED, label: 'Currently Submitted' },
  { value: WorkStatus.PUBLISHED, label: 'Published' }
]

export default function WorkEditor() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [wordCount, setWordCount] = useState(0)
  const isEditing = !!id

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isDirty }
  } = useForm<WorkForm>({
    resolver: zodResolver(workSchema),
    defaultValues: {
      title: '',
      type: WorkType.POEM,
      content: '',
      status: WorkStatus.DRAFT
    }
  })

  const content = watch('content')

  useEffect(() => {
    if (content) {
      const words = content.trim().split(/\s+/).filter(word => word.length > 0)
      setWordCount(words.length)
    } else {
      setWordCount(0)
    }
  }, [content])

  useEffect(() => {
    if (isEditing) {
      // TODO: Load work data
      // For now, use mock data
      setValue('title', 'Sample Work')
      setValue('type', WorkType.POEM)
      setValue('content', 'Sample content...')
      setValue('status', WorkStatus.DRAFT)
    }
  }, [id, isEditing, setValue])

  const onSubmit = async (data: WorkForm) => {
    setIsLoading(true)
    try {
      // TODO: Save work via API
      console.log('Saving work:', data)
      navigate('/works')
    } catch (error) {
      console.error('Error saving work:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleQuickSubmit = () => {
    // TODO: Show submission modal/page
    console.log('Quick submit to journals')
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/works')}
            className="text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-3xl font-bold text-gray-900">
            {isEditing ? 'Edit Work' : 'New Work'}
          </h1>
        </div>

        <div className="flex items-center space-x-3">
          {isEditing && (
            <button
              type="button"
              onClick={handleQuickSubmit}
              className="btn btn-secondary flex items-center space-x-2"
            >
              <Send size={18} />
              <span>Submit to Journals</span>
            </button>
          )}
          <button
            type="submit"
            form="work-form"
            disabled={isLoading || !isDirty}
            className="btn btn-primary flex items-center space-x-2"
          >
            <Save size={18} />
            <span>{isLoading ? 'Saving...' : 'Save'}</span>
          </button>
        </div>
      </div>

      <form id="work-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            <div>
              <label htmlFor="title" className="label">
                Title
              </label>
              <input
                {...register('title')}
                type="text"
                id="title"
                className="input"
                placeholder="Enter work title"
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="content" className="label">
                Content
              </label>
              <textarea
                {...register('content')}
                id="content"
                rows={20}
                className="input resize-none font-mono"
                placeholder="Start writing your literary work here..."
              />
              {errors.content && (
                <p className="mt-1 text-sm text-red-600">{errors.content.message}</p>
              )}
              <div className="mt-2 flex justify-between items-center text-sm text-gray-500">
                <span>Word count: {wordCount.toLocaleString()}</span>
                {content && (
                  <span>
                    Characters: {content.length.toLocaleString()} 
                    (with spaces) | {content.replace(/\s/g, '').length.toLocaleString()} 
                    (without spaces)
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Work Details</h3>
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="type" className="label">
                    Type
                  </label>
                  <select
                    {...register('type')}
                    id="type"
                    className="input"
                  >
                    {workTypeOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {errors.type && (
                    <p className="mt-1 text-sm text-red-600">{errors.type.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="status" className="label">
                    Status
                  </label>
                  <select
                    {...register('status')}
                    id="status"
                    className="input"
                  >
                    {statusOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {errors.status && (
                    <p className="mt-1 text-sm text-red-600">{errors.status.message}</p>
                  )}
                </div>
              </div>
            </div>

            {isEditing && (
              <div className="card">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Submission History</h3>
                <div className="text-sm text-gray-600">
                  <p>No submissions yet</p>
                  <button
                    type="button"
                    onClick={handleQuickSubmit}
                    className="mt-2 text-primary-600 hover:text-primary-900"
                  >
                    Submit to journals →
                  </button>
                </div>
              </div>
            )}

            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Writing Tips</h3>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>• Save your work frequently</li>
                <li>• Consider your target audience</li>
                <li>• Research appropriate journals</li>
                <li>• Follow submission guidelines</li>
                <li>• Keep track of deadlines</li>
              </ul>
            </div>
          </div>
        </div>
      </form>
    </div>
  )
}